import {
  GoabContainer,
  GoabDropdown,
  GoabDropdownItem,
  GoabDropdownOnChangeDetail,
  GoabFormItem,
  GoabLink,
  GoabText,
} from '@abgov/angular-components';
import { Component, computed, effect, input, signal } from '@angular/core';
import {
  ContainerShadowStyleDirective,
  DropdownIconAccessibilityDirective,
} from '../../directives';
import { formatPhoneDisplay, toTelHref } from '../../utils/phone-format.util';

export type ZoneContact = {
  key: string;
  label: string;
  phones: string[];
  hours: string;
};

/**
 * Zone picker + phone contact card, shared by any "call your zone" flow (e.g.
 * the facility apply-through-case-manager modal and the guided pathway
 * questionnaire's access centre modal). Callers own the surrounding
 * modal/drawer, heading and any intro copy; this only renders the zone map
 * link, the dropdown, and the selected zone's contact details.
 */
@Component({
  selector: 'common-zone-contact-lookup',
  standalone: true,
  imports: [
    GoabContainer,
    GoabDropdown,
    GoabDropdownItem,
    GoabFormItem,
    GoabLink,
    GoabText,
    ContainerShadowStyleDirective,
    DropdownIconAccessibilityDirective,
  ],
  templateUrl: './zone-contact-lookup.component.html',
  styleUrl: './zone-contact-lookup.component.scss',
})
export class ZoneContactLookupComponent {
  zoneContacts = input.required<ZoneContact[]>();
  zoneMapPdfUrl = input.required<string>();
  /** Whenever this value changes, the current selection is cleared (e.g. bind to the host modal's `open()`). */
  resetTrigger = input<unknown>(null);

  protected readonly selectedZoneKey = signal<string | null>(null);

  protected readonly selectedZone = computed((): ZoneContact | null => {
    const key = this.selectedZoneKey();
    if (!key) return null;
    return this.zoneContacts().find((zone) => zone.key === key) ?? null;
  });

  protected readonly zoneSelectLabel = 'Find your zone contact details';
  protected readonly zoneSelectPlaceholder = 'Select your zone';
  protected readonly zoneSelectHelpText =
    'Select a zone above to see the contact details for your zone.';
  protected readonly zoneMapLinkText = 'Find the right zone to call (PDF)';
  protected readonly zoneMapLinkAriaLabel =
    'Find the right zone to call (PDF, opens in new window)';

  // Clears the selection so each reopen (or zone-list change) starts with no zone chosen.
  protected readonly resetZoneSelection = effect(() => {
    this.resetTrigger();
    this.zoneContacts();
    this.selectedZoneKey.set(null);
  });

  protected onZoneChange(event: GoabDropdownOnChangeDetail): void {
    this.selectedZoneKey.set(event.value || null);
  }

  protected hydratePhoneDisplay(rawPhoneValue: string): string {
    return formatPhoneDisplay(rawPhoneValue);
  }

  protected toTelHref(rawPhoneValue: string): string {
    return toTelHref(rawPhoneValue);
  }
}
